import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import * as THREE from "three";
import PortfolioSection from "./PortfolioSection";
import { TypeAnimation } from "react-type-animation";
import CurvedLoop from "./Components/CurvedLoop";
import LiquidEther from "./Components/LiquidEther";
import { SiAdobephotoshop, SiAndroid, SiAndroidstudio, SiApple, SiBootstrap, SiCanva, SiCoreldraw, SiCss3, SiFigma, SiGit, SiGoogleplay, SiHtml5, SiJavascript, SiJira, SiNextdotjs, SiNotion, SiNpm, SiPostman, SiReact, SiRedux, SiTailwindcss, SiTypescript, SiVercel } from "react-icons/si";
import LogoLoop from "./Components/LogoLoop";
import ContactSection from "./ContactSection";
import { gsap } from "gsap";
import CreativePlayground from "./Components/CreativePlayground";
import ImpactMetrics from "./Components/ImpactMetrics";
import { useNavigate } from "react-router-dom";
import IkiTube from "./Components/IkiTube";
import { File, FilePenLine, Image, ImageUp, Scan, Sparkle, Wrench } from "lucide-react";
import ToolsModal from "./Components/ToolsModal";

// ─── Palette ────────────────────────────────────────────────────────────────
// #0B3D2E  deep forest (darkest bg accent)
// #145A43  mid forest  (card / metric bg)
// #2F8F6F  sage green  (primary accent)
// #D7C7A1  warm sand   (highlight numbers / glow)
// #0F0F0F  near-black  (canvas bg)
// ────────────────────────────────────────────────────────────────────────────

const Home = () => {
  const vantaRef = useRef(null);
  const vantaRef2 = useRef(null);
  const contentRef = useRef(null);

  const [vantaEffect, setVantaEffect] = useState(null);
  const [vantaEffect2, setVantaEffect2] = useState(null);

  const [showTools, setShowTools] = useState(false);

  // ── Hero Section – NET/WAVES Effect (green palette) ──────────────────────
  useEffect(() => {
    if (!vantaEffect && vantaRef.current) {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setClearColor(0x000000, 1);
      vantaRef.current.appendChild(renderer.domElement);

      const points = [];
      const numPoints = 150;

      for (let i = 0; i < numPoints; i++) {
        points.push(new THREE.Vector3(
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 30,
          (Math.random() - 0.5) * 30
        ));
      }

      const pointGeometry = new THREE.SphereGeometry(0.05, 8, 6);
      const pointMaterial = new THREE.MeshBasicMaterial({
        color: 0x2F8F6F, // sage green ← was 0x6208ff
        transparent: true,
        opacity: 0.8
      });

      const pointMeshes = points.map(point => {
        const mesh = new THREE.Mesh(pointGeometry, pointMaterial);
        mesh.position.copy(point);
        scene.add(mesh);
        return mesh;
      });

      const lineGeometry = new THREE.BufferGeometry();
      const linePositions = [];
      const lineColors = [];

      for (let i = 0; i < points.length; i++) {
        for (let j = i + 1; j < points.length; j++) {
          const distance = points[i].distanceTo(points[j]);
          if (distance < 8) {
            linePositions.push(points[i].x, points[i].y, points[i].z);
            linePositions.push(points[j].x, points[j].y, points[j].z);

            const intensity = 1 - (distance / 8);
            // RGB for #2F8F6F = (0.184, 0.561, 0.435) ← was purple (0.38, 0.03, 1)
            lineColors.push(0.184, 0.561, 0.435, intensity);
            lineColors.push(0.184, 0.561, 0.435, intensity);
          }
        }
      }

      lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
      lineGeometry.setAttribute('color', new THREE.Float32BufferAttribute(lineColors, 4));

      const lineMaterial = new THREE.LineBasicMaterial({
        vertexColors: true,
        transparent: true,
        opacity: 0.6
      });

      const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
      scene.add(lineSegments);

      const ambientLight = new THREE.AmbientLight(0x404040, 0.8);
      scene.add(ambientLight);

      const pointLight = new THREE.PointLight(0x2F8F6F, 1, 100); // ← was 0x6208ff
      pointLight.position.set(10, 10, 10);
      scene.add(pointLight);

      camera.position.set(0, 0, 20);

      let time = 0;

      const animate = () => {
        requestAnimationFrame(animate);
        time += 0.005;

        pointMeshes.forEach((mesh, i) => {
          mesh.position.y = points[i].y + Math.sin(time + i * 0.1) * 2;
          mesh.position.x = points[i].x + Math.cos(time + i * 0.05) * 1;
          mesh.rotation.x += 0.01;
          mesh.rotation.y += 0.01;
        });

        lineSegments.rotation.y += 0.002;
        lineSegments.rotation.x += 0.001;

        renderer.render(scene, camera);
      };

      animate();

      const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };

      window.addEventListener('resize', handleResize);

      setVantaEffect({
        destroy: () => {
          window.removeEventListener('resize', handleResize);
          if (vantaRef.current && renderer.domElement) {
            vantaRef.current.removeChild(renderer.domElement);
          }
          renderer.dispose();
          pointGeometry.dispose();
          pointMaterial.dispose();
          lineGeometry.dispose();
          lineMaterial.dispose();
        }
      });
    }

    return () => {
      if (vantaEffect) vantaEffect.destroy();
    };
  }, [vantaEffect]);

  // ── Creative Space Section – BIRDS/TOPOLOGY Effect (green palette) ────────
  useEffect(() => {
    if (!vantaEffect2 && vantaRef2.current) {
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
      const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });

      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setClearColor(0x000000, 0);
      vantaRef2.current.appendChild(renderer.domElement);

      const birds = [];
      const numBirds = 100;

      for (let i = 0; i < numBirds; i++) {
        const geometry = new THREE.ConeGeometry(0.3, 1.8, 4);
        // Hue shifted to green-teal range (0.35–0.55) ← was full-spectrum random
        const material = new THREE.MeshPhongMaterial({
          color: new THREE.Color().setHSL(0.35 + Math.random() * 0.2, 0.7, 0.5),
          transparent: true,
          opacity: 0.8
        });

        const bird = new THREE.Mesh(geometry, material);

        bird.position.set(
          (Math.random() - 0.5) * 60,
          (Math.random() - 0.5) * 30,
          (Math.random() - 0.5) * 40
        );

        bird.velocity = new THREE.Vector3(
          (Math.random() - 0.5) * 0.1,
          (Math.random() - 0.5) * 0.05,
          (Math.random() - 0.5) * 0.1
        );

        scene.add(bird);
        birds.push(bird);
      }

      const shapes = [];
      const numShapes = 30;

      for (let i = 0; i < numShapes; i++) {
        const geometries = [
          new THREE.TetrahedronGeometry(0.5),
          new THREE.OctahedronGeometry(0.4),
          new THREE.IcosahedronGeometry(0.3)
        ];

        const geometry = geometries[Math.floor(Math.random() * geometries.length)];
        // Wireframe shapes in deep forest / sage tones ← was purple range
        const material = new THREE.MeshPhongMaterial({
          color: new THREE.Color().setHSL(0.38 + Math.random() * 0.15, 0.8, 0.35),
          transparent: true,
          opacity: 0.6,
          wireframe: true
        });

        const shape = new THREE.Mesh(geometry, material);

        shape.position.set(
          (Math.random() - 0.5) * 50,
          (Math.random() - 0.5) * 25,
          (Math.random() - 0.5) * 35
        );

        shape.rotationSpeed = {
          x: (Math.random() - 0.5) * 0.02,
          y: (Math.random() - 0.5) * 0.02,
          z: (Math.random() - 0.5) * 0.02
        };

        scene.add(shape);
        shapes.push(shape);
      }

      const ambientLight = new THREE.AmbientLight(0x404040, 0.6);
      scene.add(ambientLight);

      const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8);
      directionalLight.position.set(10, 10, 5);
      scene.add(directionalLight);

      // Point lights: deep forest green + warm sand ← was magenta + cyan
      const pointLight1 = new THREE.PointLight(0x0B3D2E, 0.5, 50);
      pointLight1.position.set(-10, 10, -10);
      scene.add(pointLight1);

      const pointLight2 = new THREE.PointLight(0xD7C7A1, 0.5, 50);
      pointLight2.position.set(10, -10, 10);
      scene.add(pointLight2);

      camera.position.set(0, 0, 25);

      let time = 0;

      const animate = () => {
        requestAnimationFrame(animate);
        time += 0.01;

        birds.forEach((bird, i) => {
          bird.position.add(bird.velocity);

          if (bird.position.x > 30) bird.position.x = -30;
          if (bird.position.x < -30) bird.position.x = 30;
          if (bird.position.y > 15) bird.position.y = -15;
          if (bird.position.y < -15) bird.position.y = 15;
          if (bird.position.z > 20) bird.position.z = -20;
          if (bird.position.z < -20) bird.position.z = 20;

          bird.lookAt(bird.position.clone().add(bird.velocity.clone().multiplyScalar(10)));

          bird.velocity.add(new THREE.Vector3(
            (Math.random() - 0.5) * 0.002,
            (Math.random() - 0.5) * 0.002,
            (Math.random() - 0.5) * 0.002
          ));

          bird.velocity.clampLength(0.02, 0.08);
          bird.scale.y = 1 + Math.sin(time * 10 + i) * 0.2;
        });

        shapes.forEach(shape => {
          shape.rotation.x += shape.rotationSpeed.x;
          shape.rotation.y += shape.rotationSpeed.y;
          shape.rotation.z += shape.rotationSpeed.z;
          shape.position.y += Math.sin(time + shape.position.x * 0.01) * 0.01;
        });

        pointLight1.position.x = Math.sin(time) * 15;
        pointLight1.position.z = Math.cos(time) * 15;
        pointLight2.position.x = Math.cos(time * 0.7) * 12;
        pointLight2.position.z = Math.sin(time * 0.7) * 12;

        renderer.render(scene, camera);
      };

      animate();

      const handleResize = () => {
        camera.aspect = window.innerWidth / window.innerHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };

      window.addEventListener('resize', handleResize);

      setVantaEffect2({
        destroy: () => {
          window.removeEventListener('resize', handleResize);
          if (vantaRef2.current && renderer.domElement) {
            vantaRef2.current.removeChild(renderer.domElement);
          }
          renderer.dispose();
          birds.forEach(bird => {
            bird.geometry.dispose();
            bird.material.dispose();
          });
          shapes.forEach(shape => {
            shape.geometry.dispose();
            shape.material.dispose();
          });
        }
      });
    }

    return () => {
      if (vantaEffect2) vantaEffect2.destroy();
    };
  }, [vantaEffect2]);

  // ── GSAP entrance animations (unchanged) ─────────────────────────────────
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.timeline()
        .from('.hero-title', {
          opacity: 0,
          y: 60,
          duration: 1.2,
          ease: 'power4.out',
          delay: 0.3
        })
        .from('.hero-subtitle', {
          opacity: 0,
          y: 40,
          duration: 1,
          ease: 'power3.out'
        }, '-=0.9')
        .from('.hero-description', {
          opacity: 0,
          y: 30,
          duration: 0.8,
          ease: 'power3.out'
        }, '-=0.7')
        .from('.hero-tools', {
          opacity: 0,
          y: 30,
          scale: 0.85,
          duration: 0.8,
          ease: 'back.out(1.7)',
          clearProps: 'all'
        }, '-=0.2');
    });

    return () => ctx.revert();
  }, []);

  const techLogos = [
    { node: <SiReact />, title: "React", href: "https://react.dev" },
    { node: <SiNextdotjs />, title: "Next.js", href: "https://nextjs.org" },
    { node: <SiTypescript />, title: "TypeScript", href: "https://www.typescriptlang.org" },
    { node: <SiJavascript />, title: "JavaScript", href: "https://javascript.com" },
    { node: <SiRedux />, title: "Redux", href: "https://redux.js.org" },
    { node: <SiFigma />, title: "Figma", href: "https://figma.com" },
    { node: <SiPostman />, title: "Postman", href: "https://postman.com" },
    { node: <SiVercel />, title: "Vercel", href: "https://vercel.com" },
    { node: <SiNpm />, title: "npm", href: "https://npmjs.com" },
    { node: <SiCoreldraw />, title: "CorelDRAW", href: "https://coreldraw.com" },
    { node: <SiAdobephotoshop />, title: "Photoshop", href: "https://adobe.com/products/photoshop.html" },
    { node: <SiCanva />, title: "Canva", href: "https://canva.com" },
    { node: <SiGit />, title: "Git", href: "https://git-scm.com" },
    { node: <SiJira />, title: "Jira", href: "https://jira.com" },
    { node: <SiNotion />, title: "Notion", href: "https://notion.so" },
    { node: <SiHtml5 />, title: "HTML5", href: "https://html5.org" },
    { node: <SiCss3 />, title: "CSS3", href: "https://css3.com" },
    { node: <SiAndroidstudio />, title: "Android Studio", href: "https://developer.android.com/studio" },
    { node: <SiApple />, title: "Apple", href: "https://apple.com" },
    { node: <SiAndroid />, title: "Android", href: "https://android.com" },
    { node: <SiBootstrap />, title: "Bootstrap", href: "https://bootstrap.com" },
    { node: <SiTailwindcss />, title: "Tailwind CSS", href: "https://tailwindcss.com" },
    { node: <SiGoogleplay />, title: "Google Play", href: "https://play.google.com" },
  ];

  const navigate = useNavigate();

  return (
    <>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative h-screen">
        <div style={{ width: '100%', height: "100%", position: 'relative' }} className="bg-[#0F0F0F]">
          {/* LiquidEther: purple trio → forest green + warm sand */}
          <LiquidEther
            colors={['#0B3D2E', '#2F8F6F', '#D7C7A1']}
            mouseForce={35}
            cursorSize={100}
            isViscous={false}
            viscous={30}
            iterationsViscous={32}
            iterationsPoisson={32}
            resolution={0.65}
            isBounce={false}
            autoDemo={true}
            autoSpeed={0.5}
            autoIntensity={2.2}
            takeoverDuration={0.25}
            autoResumeDelay={3000}
            autoRampDuration={0.6}
          />
        </div>

        <div className="absolute inset-0 z-10 flex items-center justify-start px-8 md:px-20">
          <div ref={contentRef} className="w-full md:max-w-2xl">

            <h1 className="hero-title text-white text-6xl font-extrabold leading-tight mb-4">
              Hi, I'm Iki<span className="text-[#2F8F6F]">.</span>
            </h1>

            <h2 className="hero-subtitle text-white text-xl md:text-4xl font-semibold mb-4 min-h-[3rem]">
              I'm a{' '}
              <TypeAnimation
                sequence={[
                  'Frontend Developer', 1800,
                  'Full-Stack Developer', 1800,
                  'Designer Graphic', 1800,
                  'Software Engineer', 1600,
                  'React & Next.js Specialist', 2000,
                  'Mobile & Web Creator', 1800,
                ]}
                wrapper="span"
                speed={45}
                deletionSpeed={65}
                repeat={Infinity}
                cursor={true}
                className="text-[#2F8F6F] font-bold"
              />
            </h2>

            <p className="hero-description text-xs md:text-lg text-slate-300 mb-6 leading-relaxed">
              Passionate about building <span className="text-[#2F8F6F] font-bold">impactful</span>, fast and beautiful
              web & mobile applications using modern tech.
            </p>

            <div className="hero-tools">
              <div className="hero-tools">
                <button
                  onClick={() => setShowTools(true)}
                  className="
      group
      px-3 md:px-6 py-2 md:py-3
      rounded-full
      bg-white/10
      backdrop-blur-xl
      border border-[#2F8F6F]/30
      text-white
      shadow-lg shadow-[#145A43]/20
      hover:bg-white/20
      hover:border-[#2F8F6F]/60
      hover:shadow-[#2F8F6F]/30
      hover:scale-105
      transition-all duration-300
      flex items-center gap-2
      leading-relaxed
      text-sm
      font-extrabold
    "
                >
                  <span> Iki's Free Tools</span>
                  <Wrench size={20} />
                </button>
              </div>

              <ToolsModal
                open={showTools}
                onClose={() => setShowTools(false)}
                onNavigate={(route) => navigate(route)}
              />
            </div>

            {/* <div
              className={`
                overflow-hidden
                transition-all duration-500 ease-out
                ${showTools ? "opacity-100 mt-4" : "max-h-0 opacity-0"}
              `}
            >
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => navigate('iki-remove-bg')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "

                >
                  <Image size={14} />
                  Remove Background Image
                </button>

                <button
                  onClick={() => navigate('iki-file-converter')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "
                >
                  <File size={14} />
                  Format File Converter
                </button>

                <button
                  onClick={() => navigate('iki-scan-to-pdf')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "
                >
                  <Scan size={14} />
                  Scan To PDF
                </button>

                <button
                  onClick={() => navigate('iki-image-upscaler')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "
                >
                  <ImageUp size={14} />
                  Image Upscaler
                  <span className="py-[2px] px-2 flex flex-roe gap-1 bg-green-700/30 rounded-full items-center text-[8px]">
                    <Wrench size={10} />
                    Beta v0.8
                  </span>
                </button>

                <button
                  onClick={() => navigate('iki-signer-doc')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "
                >
                  <FilePenLine size={14} />
                  Document Signer
                  <span className="py-[2px] px-2 flex flex-roe gap-1 bg-blue-700/30 rounded-full items-center text-[8px]">
                    <Sparkle size={10} />
                    New v1.0
                  </span>
                </button>
                <button
                  onClick={() => navigate('iki-image-vector-converter')}
                  className="
                  px-3 md:px-6 py-2 md:py-3
                    rounded-full
                    bg-white/10
                    backdrop-blur-xl
                    border border-[#2F8F6F]/30
                    text-[#D7C7A1]
                    hover:bg-[#2F8F6F]/20
                    hover:text-white
                    hover:border-[#2F8F6F]
                    transition-all duration-300
                    shadow-md shadow-[#145A43]/20
                    text-[8px] md:text-xs
                    font-bold
                    flex flex-row gap-2 items-center align-middle
                  "
                >
                  <Image size={14} />
                  Image to Vector Converter
                  <span className="py-[2px] px-2 flex flex-roe gap-1 bg-green-700/30 rounded-full items-center text-[8px]">
                    <Wrench size={10} />
                    Beta v0.1
                  </span>
                </button>
              </div>
            </div> */}
          </div>
        </div>
      </div>

      <div className="relative">
        <CurvedLoop
          marqueeText="Welcome ✦ to ✦ My ✦ Creative ✦ Space ✦"
          speed={3}
          curveAmount={500}
          direction="right"
          interactive={true}
          className="custom-text-style"
        />
      </div>

      <CreativePlayground />

      <IkiTube />

      <ImpactMetrics />

      <PortfolioSection />

      <ContactSection />

      <div className="relative bg-[#0F0F0F] pt-24 pb-10">
        <LogoLoop
          logos={techLogos}
          speed={120}
          direction="right"
          logoHeight={28}
          gap={40}
          hoverSpeed={0}
          scaleOnHover
          fadeOut
          fadeOutColor="#0F0F0F"
          ariaLabel="Technology partners"
        />
      </div>

      <div className="text-gray-400 flex w-full justify-center text-sm text-end md:text-base bg-[#0F0F0F] pb-10">
        <p>Copyright © 2025 Rizki Makruf. All rights reserved.</p>
      </div>

      <ToolsModal
        open={showTools}
        onClose={() => setShowTools(false)}
        onNavigate={(route) => navigate(route)}
      />
    </>
  );
};

export default Home;